import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import ExternalMemberForm from '../panels/ExternalMemberForm';
import ExternalMembersListRows from '../panels/ExternalMembersListRows';
import externalMemberService from '../../../services/externalMemberService';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
  ReceptionPageHeader,
  ReceptionLoadingBlock,
  ReceptionEmptyState,
  ReceptionModalShell
} from '../ui';
import BrowseSearchBar from '../browse/BrowseSearchBar';

const emptyForm = {
  nom: '',
  prenom: '',
  date_naissance: '',
  sexe: '',
  telephone: '',
  email: '',
  adresse: ''
};

export default function ReceptionExternalMembersPage() {
  const { toast } = useOutletContext();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, limit: 15, offset: 0 });
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const limit = 15;
  const isSearching = search.trim() !== debouncedSearch.trim();
  const isFiltered = Boolean(debouncedSearch.trim());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const offset = page * limit;
      const res = await externalMemberService.list({
        search: debouncedSearch || undefined,
        limit,
        offset
      });
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
        setPagination(res.pagination || { total: 0, totalPages: 1, limit, offset });
      } else {
        toast('danger', res.message || 'Chargement impossible');
      }
    } catch (e) {
      toast('danger', e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, limit, page, toast]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const totalCount = pagination.total || 0;
  const countLabel = loading
    ? 'Chargement…'
    : `${totalCount} membre${totalCount > 1 ? 's' : ''} externe${totalCount > 1 ? 's' : ''}`;

  const rangeLabel = useMemo(() => {
    if (!totalCount) return null;
    const from = page * limit + 1;
    const to = Math.min(totalCount, (page + 1) * limit);
    return `${from}–${to}`;
  }, [page, totalCount, limit]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        date_naissance: form.date_naissance || null,
        sexe: form.sexe || null,
        telephone: form.telephone?.trim() || null,
        email: form.email?.trim() || null,
        adresse: form.adresse?.trim() || null
      };
      if (editingId) {
        await externalMemberService.update(editingId, payload);
        toast('success', 'Membre externe mis à jour.');
      } else {
        await externalMemberService.create(payload);
        toast('success', 'Membre externe créé.');
      }
      resetForm();
      await loadData();
    } catch (e2) {
      toast('danger', e2.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nom: row.nom || '',
      prenom: row.prenom || '',
      date_naissance: row.date_naissance ? String(row.date_naissance).slice(0, 10) : '',
      sexe: row.sexe || '',
      telephone: row.telephone || '',
      email: row.email || '',
      adresse: row.adresse || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = (row) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await externalMemberService.remove(deleteTarget.id);
      toast('success', 'Membre externe supprimé.');
      if (editingId === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      await loadData();
    } catch (e) {
      toast('danger', e.message || 'Suppression impossible');
    } finally {
      setDeleting(false);
    }
  };

  const listHead = (
    <div className="recv-browse-list__head" aria-hidden="true">
      <span className="recv-browse-list__cell recv-browse-list__cell--lead">Personne</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--contact">Contact</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--action">Actions</span>
    </div>
  );

  const renderPagination = (position = 'bottom') => {
    if (loading || totalCount <= 0) return null;
    const multiPage = totalPages > 1;
    return (
      <div
        className={`recv-journal-pagination recv-browse-pagination${position === 'top' ? ' mb-3' : ' mt-0 border-top'}`}
        aria-label="Pagination des membres externes"
      >
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page <= 0 || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Précédent
        </Button>
        <div className="recv-journal-pagination__info">
          Page <strong>{page + 1}</strong> / {totalPages}
          {' · '}
          <strong>{totalCount}</strong> résultat{totalCount > 1 ? 's' : ''}
          {rangeLabel ? (
            <>
              {' '}
              (lignes {rangeLabel})
            </>
          ) : null}
          {!multiPage ? (
            <span className="d-block small mt-1">Tous les résultats sont sur cette page.</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page >= totalPages - 1 || loading}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Suivant →
        </Button>
      </div>
    );
  };

  return (
    <div className="recv-dash-pro recv-dash-pro--main">
      <ReceptionPageHeader
        badge="Hors SMIE"
        title="Membres externes"
        description="Création et gestion des patients hors SMIE — recherche rapide et actions directes."
      />

      <Row className="g-4">
        <Col xs={12}>
          <Card
            className={`border-0 recv-dash-card recv-dash-card--elevated recv-browse-form-card${editingId ? ' is-editing' : ''}`}
          >
            <Card.Header className="recv-browse-form-card__header border-bottom-0 pb-0">
              <div className="recv-browse-form-card__hero">
                <div>
                  <div className="recv-browse-panel__eyebrow">
                    {editingId ? 'Édition en cours' : 'Enregistrement'}
                  </div>
                  <div className="recv-browse-form-card__title">
                    {editingId ? 'Modifier membre externe' : 'Nouveau membre externe'}
                  </div>
                  <p className="recv-browse-panel__desc mb-0">
                    {editingId
                      ? 'Mettez à jour les coordonnées puis enregistrez.'
                      : 'Saisissez les informations de base pour créer un dossier externe.'}
                  </p>
                </div>
                {editingId ? (
                  <Badge bg="warning" text="dark" className="recv-browse-form-card__badge">
                    Édition #{editingId}
                  </Badge>
                ) : null}
              </div>
            </Card.Header>
            <Card.Body className="pt-3">
              <ExternalMemberForm
                value={form}
                onChange={setForm}
                onSubmit={submit}
                onCancel={resetForm}
                saving={saving}
                editing={!!editingId}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--browse recv-panel-card--browse-external">
            <Card.Header className="recv-browse-panel__header border-bottom-0 pb-0">
              <div className="recv-browse-panel__hero">
                <div className="recv-browse-panel__intro">
                  <div className="recv-browse-panel__eyebrow">Registre externe</div>
                  <div className="recv-browse-panel__title">Liste des membres</div>
                  <p className="recv-browse-panel__desc mb-0">
                    Recherche par nom, prénom ou téléphone — {countLabel}.
                  </p>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="recv-browse-panel__body pt-0">
              <BrowseSearchBar
                id="recv-external-search"
                label="Rechercher un membre externe"
                placeholder="Nom, prénom, téléphone…"
                value={search}
                onChange={setSearch}
                searching={isSearching}
                hint={
                  isSearching
                    ? 'Recherche en cours…'
                    : isFiltered
                      ? `${totalCount} résultat${totalCount > 1 ? 's' : ''} pour cette recherche.`
                      : 'Trouvez rapidement un patient hors SMIE déjà enregistré.'
                }
              />

              {loading ? (
                <ReceptionLoadingBlock label="Chargement des membres externes…" />
              ) : items.length === 0 ? (
                <ReceptionEmptyState
                  title={isFiltered ? 'Aucun résultat' : 'Aucun membre externe'}
                  description={
                    isFiltered
                      ? 'Modifiez la recherche ou effacez le filtre.'
                      : 'Créez un premier membre avec le formulaire ci-dessus.'
                  }
                  action={
                    isFiltered ? (
                      <Button variant="outline-primary" size="sm" onClick={() => setSearch('')}>
                        Effacer la recherche
                      </Button>
                    ) : null
                  }
                />
              ) : (
                <>
                  {renderPagination('top')}
                  <div className="recv-browse-list-wrap recv-browse-list-wrap--external border rounded bg-white mt-3">
                    <div className="recv-browse-list" role="list" aria-label="Liste des membres externes">
                      {listHead}
                      <ExternalMembersListRows items={items} onEdit={onEdit} onDelete={onDelete} />
                    </div>
                  </div>
                  {renderPagination('bottom')}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ReceptionModalShell
        show={!!deleteTarget}
        onHide={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        title="Supprimer ce membre externe ?"
        subtitle="Cette action retire le dossier externe de la liste de réception."
        size="md"
        disableClose={deleting}
        footer={
          <>
            <Button
              variant="outline-secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Annuler
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <p className="mb-0">
          Confirmez la suppression de{' '}
          <strong>
            {deleteTarget?.nom} {deleteTarget?.prenom || ''}
          </strong>
          .
        </p>
      </ReceptionModalShell>
    </div>
  );
}
